/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from "@wordpress/i18n";

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { InspectorControls, useBlockProps } from "@wordpress/block-editor";
import { PanelBody, TextControl, ToggleControl } from "@wordpress/components";
import { useEffect } from "react";

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit({ attributes, setAttributes }) {
	const { fallbackCurrentYear, showStartingYear, startingYear, copyrightNote } =
		attributes;
	const currentYear = new Date().getFullYear().toString();

	useEffect(() => {
		if (currentYear !== fallbackCurrentYear) {
			setAttributes({ fallbackCurrentYear: currentYear });
		}
	}, [currentYear, fallbackCurrentYear, setAttributes]);

	// Default copyright note if empty
	const displayCopyrightNote = copyrightNote || "All rights reserved";

	let displayDate;

	if (showStartingYear && startingYear) {
		displayDate =
			startingYear + "– " + currentYear + " " + displayCopyrightNote;
	} else {
		displayDate = currentYear + " " + displayCopyrightNote;
	}

	return (
		<>
			<InspectorControls>
				<PanelBody title={__("Settings", "copyright-date-block")}>
					<ToggleControl
						checked={!!showStartingYear}
						label={__("Show starting year", "copyright-date-block")}
						onChange={() =>
							setAttributes({
								showStartingYear: !showStartingYear,
							})
						}
					/>
					{showStartingYear && (
						<TextControl
							label={__("Starting year", "copyright-date-block")}
							value={startingYear || ""}
							onChange={(value) => setAttributes({ startingYear: value })}
						/>
					)}
					<TextControl
						label={__("Copyright note", "copyright-date-block")}
						value={copyrightNote || ""}
						placeholder="All rights reserved"
						help={__(
							"You can use HTML tags like <a> for links",
							"copyright-date-block",
						)}
						onChange={(value) => setAttributes({ copyrightNote: value })}
					/>
				</PanelBody>
			</InspectorControls>
			<p {...useBlockProps()}>
				© <span dangerouslySetInnerHTML={{ __html: displayDate }} />
			</p>
		</>
	);
}
